import React, { useState, useEffect } from 'react';
import {
  getTodayCashSession, getCashSessions,
  openCashSession, closeCashSession,
  getCashTransactions, createCashTransaction, deleteCashTransaction,
  getProducts, exportCashReport
} from '../lib/api';
import { getStoredToken } from '../lib/authStorage';
import { formatCurrency, getTodayDate, paymentMethods } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Wallet, Plus, TrendingUp, TrendingDown,
  CheckCircle, Loader2, CreditCard, Banknote,
  ArrowRightLeft, Trash2, DollarSign, Download,
  Calendar, ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const Caja = () => {
  const [session, setSession] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [history, setHistory] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showTxnDialog, setShowTxnDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [openingCash, setOpeningCash] = useState('');
  const [closingCash, setClosingCash] = useState('');
  const [closingObs, setClosingObs] = useState('');
  const [txnType, setTxnType] = useState('ingreso');
  const [txnData, setTxnData] = useState({
    item: '', product_id: '', categoria: '', medio_pago: 'efectivo', cantidad: '1',
    monto: '', numero_transaccion: '', boleta: '', observaciones: ''
  });
  const [saving, setSaving] = useState(false);
  const [histMonth, setHistMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loadingHist, setLoadingHist] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);
  const [sessionTxns, setSessionTxns] = useState([]);
  const [loadingTxns, setLoadingTxns] = useState(false);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchHistory(); }, [histMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [sessionRes, productsRes] = await Promise.all([
        getTodayCashSession(),
        getProducts()
      ]);
      const s = sessionRes.data;
      setSession(s);
      setProducts(productsRes.data || []);
      if (s) {
        const txnRes = await getCashTransactions({ session_id: s.id });
        setTransactions(txnRes.data);
      }
    } catch (error) {
      console.error('Error cargando caja:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHist(true);
    try {
      const [y, m] = histMonth.split('-');
      const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
      const res = await getCashSessions(60, `${histMonth}-01`, `${histMonth}-${lastDay}`);
      setHistory(res.data || []);
    } catch (e) {
      console.error('Error cargando historial:', e.message);
    } finally {
      setLoadingHist(false);
    }
  };

  const getHistDateRange = () => {
    const [y, m] = histMonth.split('-');
    const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
    return { desde: `${histMonth}-01`, hasta: `${histMonth}-${lastDay}` };
  };

  const navigateHistMonth = (dir) => {
    const [y, m] = histMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setHistMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const histMonthLabel = () => {
    const [y, m] = histMonth.split('-');
    const months = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
    return `${months[parseInt(m) - 1]} ${y}`;
  };

  const handleOpen = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await openCashSession({ efectivo_inicial: parseFloat(openingCash) || 0 });
      toast.success('Caja abierta correctamente');
      setShowOpenDialog(false);
      setOpeningCash('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al abrir caja');
    } finally { setSaving(false); }
  };

  const handleAddTxn = async (e) => {
    e.preventDefault();
    setSaving(true);
    const monto = txnType === 'cortesia' ? 0 : (parseFloat(txnData.monto) || 0);
    const cantidad = parseInt(txnData.cantidad) || 1;

    // Determine category based on product or manual gasto
    let categoria = txnData.categoria || txnType;
    if (txnType === 'cortesia') categoria = 'cortesia';
    if ((txnType === 'ingreso' || txnType === 'cortesia') && txnData.product_id) {
      const prod = products.find(p => p.id === txnData.product_id);
      if (prod) {
        categoria = txnType === 'cortesia' ? 'cortesia' : (prod.category === 'Bebestibles' ? 'frigobar' : prod.category === 'Ropa' || prod.category === 'Accesorios' ? 'merch' : 'ingreso');
      }
    }

    const payload = {
      item: txnType === 'cortesia' ? `[Cortesia] ${txnData.item || 'Cortesia'}` : txnData.item,
      categoria: categoria,
      medio_pago: txnType === 'gasto' ? 'efectivo' : (txnType === 'cortesia' ? 'efectivo' : txnData.medio_pago),
      cantidad: cantidad,
      numero_transaccion: txnData.numero_transaccion,
      boleta: txnData.boleta,
      observaciones: txnData.observaciones,
      product_id: (txnType === 'ingreso' || txnType === 'cortesia') ? txnData.product_id || null : null,
    };

    if (txnType === 'gasto') {
      payload.gasto_efectivo = monto;
      payload.ingreso_getnet = 0;
      payload.ingreso_efectivo = 0;
      payload.ingreso_transferencia = 0;
    } else {
      payload.gasto_efectivo = 0;
      if (txnData.medio_pago === 'tarjeta') {
        payload.ingreso_getnet = monto;
        payload.ingreso_efectivo = 0;
        payload.ingreso_transferencia = 0;
      } else if (txnData.medio_pago === 'transferencia') {
        payload.ingreso_getnet = 0;
        payload.ingreso_efectivo = 0;
        payload.ingreso_transferencia = monto;
      } else {
        payload.ingreso_getnet = 0;
        payload.ingreso_efectivo = monto;
        payload.ingreso_transferencia = 0;
      }
    }
    try {
      await createCashTransaction(payload);
      toast.success('Transaccion registrada');
      setShowTxnDialog(false);
      setTxnData({ item: '', product_id: '', categoria: '', medio_pago: 'efectivo', cantidad: '1', monto: '', numero_transaccion: '', boleta: '', observaciones: '' });
      setTxnType('ingreso');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar');
    } finally { setSaving(false); }
  };

  const handleDeleteTxn = async (id) => {
    if (!window.confirm('Eliminar esta transaccion?')) return;
    try {
      await deleteCashTransaction(id);
      toast.success('Transaccion eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleClose = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await closeCashSession({ efectivo_final: parseFloat(closingCash) || 0, observaciones: closingObs });
      toast.success('Caja cerrada correctamente');
      setShowCloseDialog(false);
      setClosingCash('');
      setClosingObs('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar caja');
    } finally { setSaving(false); }
  };

  const handleExportCaja = async () => {
    const { desde, hasta } = getHistDateRange();
    const url = exportCashReport(desde, hasta);
    const token = getStoredToken();
    try {
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Error');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `reporte_caja_${desde}_a_${hasta}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Reporte descargado');
    } catch (e) {
      toast.error('Error al descargar reporte');
    }
  };

  const toggleSessionDetail = async (sessionId) => {
    if (expandedSession === sessionId) {
      setExpandedSession(null);
      setSessionTxns([]);
      return;
    }
    setExpandedSession(sessionId);
    setLoadingTxns(true);
    try {
      const res = await getCashTransactions({ session_id: sessionId });
      setSessionTxns(res.data || []);
    } catch (e) {
      console.error('Error cargando transacciones:', e.message);
      toast.error('Error al cargar detalle');
    } finally { setLoadingTxns(false); }
  };

  const handleProductSelect = (productId) => {
    if (productId === 'other') {
      setTxnData(prev => ({ ...prev, product_id: '', item: '', categoria: 'ingreso' }));
      return;
    }
    const prod = products.find(p => p.id === productId);
    if (prod) {
      setTxnData(prev => ({
        ...prev,
        product_id: productId,
        item: prod.name,
        monto: prev.monto || String(prod.price),
        categoria: prod.category === 'Bebestibles' ? 'frigobar' : prod.category === 'Ropa' || prod.category === 'Accesorios' ? 'merch' : 'ingreso'
      }));
    }
  };

  const totalGetnet = transactions.reduce((s, t) => s + (t.ingreso_getnet || 0), 0);
  const totalEfectivo = transactions.reduce((s, t) => s + (t.ingreso_efectivo || 0), 0);
  const totalTransfer = transactions.reduce((s, t) => s + (t.ingreso_transferencia || 0), 0);
  const totalGastos = transactions.reduce((s, t) => s + (t.gasto_efectivo || 0), 0);
  const totalIngresos = totalGetnet + totalEfectivo + totalTransfer;
  const saldoEsperado = (session?.efectivo_inicial || 0) + totalEfectivo - totalGastos;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="caja-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="caja-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Caja Diaria</h1>
          <p className="page-subtitle">
            {getTodayDate()} - {session ? (session.estado_caja === 'abierta' ? 'Caja Abierta' : 'Caja Cerrada') : 'Sin Abrir'}
            {session && <span className="ml-2 text-xs text-[#5C7083]">Resp: {session.responsable}</span>}
          </p>
        </div>
        <div className="flex gap-3">
          {!session && (
            <Button className="btn-primary gap-2" onClick={() => setShowOpenDialog(true)} data-testid="btn-open-cash">
              <Wallet size={18} /> Abrir Caja
            </Button>
          )}
          {session?.estado_caja === 'cerrada' && (
            <Button className="btn-primary gap-2" onClick={() => setShowOpenDialog(true)} data-testid="btn-reopen-cash">
              <Wallet size={18} /> Re-abrir Caja
            </Button>
          )}
          {session?.estado_caja === 'abierta' && (
            <>
              <Button className="btn-secondary gap-2" onClick={() => setShowTxnDialog(true)} data-testid="btn-add-transaction">
                <Plus size={18} /> Nueva Transaccion
              </Button>
              <Button className="btn-primary gap-2" onClick={() => setShowCloseDialog(true)} data-testid="btn-close-cash">
                <CheckCircle size={18} /> Cerrar Caja
              </Button>
            </>
          )}
        </div>
      </div>

      {!session && (
        <div className="card text-center py-12" data-testid="no-cash-session">
          <Wallet className="mx-auto h-16 w-16 text-[#E8E2D7] mb-4" />
          <h3 className="text-lg font-semibold text-[#1A2835] mb-2">Caja no abierta</h3>
          <p className="text-[#5C7083] mb-4">Abre la caja para comenzar a registrar transacciones</p>
          <Button className="btn-primary gap-2" onClick={() => setShowOpenDialog(true)}>
            <Wallet size={18} /> Abrir Caja
          </Button>
        </div>
      )}

      {session && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="cash-stats">
            <div className="card p-4">
              <div className="flex items-center justify-between mb-1">
                <Wallet className="text-[#2B4257]" size={20} />
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${session.estado_caja === 'abierta' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {session.estado_caja === 'abierta' ? 'Abierta' : 'Cerrada'}
                </span>
              </div>
              <div className="text-xl font-bold text-[#1A2835]">{formatCurrency(session.efectivo_inicial)}</div>
              <div className="text-xs text-[#5C7083]">Efectivo Inicial</div>
            </div>
            <div className="card p-4">
              <TrendingUp className="text-[#4A7C59] mb-1" size={20} />
              <div className="text-xl font-bold text-[#4A7C59]">{formatCurrency(totalIngresos)}</div>
              <div className="text-xs text-[#5C7083]">Total Ingresos</div>
            </div>
            <div className="card p-4">
              <TrendingDown className="text-[#C25953] mb-1" size={20} />
              <div className="text-xl font-bold text-[#C25953]">{formatCurrency(totalGastos)}</div>
              <div className="text-xs text-[#5C7083]">Gastos</div>
            </div>
            <div className="card p-4">
              <Banknote className="text-[#2B4257] mb-1" size={20} />
              <div className="text-xl font-bold text-[#1A2835]">{formatCurrency(saldoEsperado)}</div>
              <div className="text-xs text-[#5C7083]">Saldo Esperado</div>
            </div>
          </div>

          {/* Payment breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card flex items-center gap-4 p-4">
              <div className="p-2 bg-[#EDF5F0] rounded-lg"><Banknote className="text-[#4A7C59]" size={20} /></div>
              <div>
                <p className="text-sm text-[#5C7083]">Efectivo</p>
                <p className="text-lg font-semibold">{formatCurrency(totalEfectivo)}</p>
              </div>
            </div>
            <div className="card flex items-center gap-4 p-4">
              <div className="p-2 bg-[#F5F0E8] rounded-lg"><CreditCard className="text-[#2B4257]" size={20} /></div>
              <div>
                <p className="text-sm text-[#5C7083]">Tarjeta (Getnet)</p>
                <p className="text-lg font-semibold">{formatCurrency(totalGetnet)}</p>
              </div>
            </div>
            <div className="card flex items-center gap-4 p-4">
              <div className="p-2 bg-[#FAF0EC] rounded-lg"><ArrowRightLeft className="text-[#D98A6C]" size={20} /></div>
              <div>
                <p className="text-sm text-[#5C7083]">Transferencia</p>
                <p className="text-lg font-semibold">{formatCurrency(totalTransfer)}</p>
              </div>
            </div>
          </div>

          {/* Transactions Table */}
          <div className="card" data-testid="transactions-table">
            <h3 className="text-lg font-semibold text-[#1A2835] mb-4">
              Transacciones del Dia
              <span className="text-sm font-normal text-[#5C7083] ml-2">({transactions.length})</span>
            </h3>
            {transactions.length === 0 ? (
              <div className="text-center py-8 text-[#5C7083]">
                <DollarSign className="mx-auto h-12 w-12 text-[#E8E2D7] mb-2" />
                <p>No hay transacciones registradas</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left">#</th>
                      <th className="text-left">Item</th>
                      <th className="text-left">Cat.</th>
                      <th className="text-left">Medio</th>
                      <th className="text-right">Getnet</th>
                      <th className="text-right">Efectivo</th>
                      <th className="text-right">Transfer.</th>
                      <th className="text-right">Gasto</th>
                      <th className="text-right font-bold">Saldo</th>
                      {session.estado_caja === 'abierta' && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t, i) => (
                      <tr key={t.id} data-testid={`txn-row-${i}`}>
                        <td className="text-[#5C7083]">{i + 1}</td>
                        <td className="font-medium">{t.item || '-'}</td>
                        <td>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#F5F0E8] text-[#5C7083]">
                            {t.categoria || '-'}
                          </span>
                        </td>
                        <td>{t.medio_pago || '-'}</td>
                        <td className="text-right text-[#2B4257]">{t.ingreso_getnet ? formatCurrency(t.ingreso_getnet) : '-'}</td>
                        <td className="text-right text-[#4A7C59]">{t.ingreso_efectivo ? formatCurrency(t.ingreso_efectivo) : '-'}</td>
                        <td className="text-right text-[#D98A6C]">{t.ingreso_transferencia ? formatCurrency(t.ingreso_transferencia) : '-'}</td>
                        <td className="text-right text-[#C25953]">{t.gasto_efectivo ? formatCurrency(t.gasto_efectivo) : '-'}</td>
                        <td className="text-right font-bold text-[#1A2835]">{formatCurrency(t.saldo_acumulado)}</td>
                        {session.estado_caja === 'abierta' && (
                          <td>
                            <button
                              onClick={() => handleDeleteTxn(t.id)}
                              className="text-[#C25953] hover:text-red-700 p-1"
                              data-testid={`btn-delete-txn-${i}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Closing info */}
          {session.estado_caja === 'cerrada' && (
            <div className="card bg-[#F5F0E8]" data-testid="closing-info">
              <h3 className="font-semibold text-[#1A2835] mb-3">Resumen de Cierre</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-[#5C7083]">Efectivo Final</p>
                  <p className="font-semibold">{formatCurrency(session.efectivo_final)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#5C7083]">Diferencia</p>
                  <p className={`font-semibold ${session.diferencia === 0 ? 'text-[#4A7C59]' : 'text-[#C25953]'}`}>
                    {formatCurrency(session.diferencia)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#5C7083]">Responsable</p>
                  <p className="font-medium">{session.responsable}</p>
                </div>
                {session.observaciones && (
                  <div>
                    <p className="text-sm text-[#5C7083]">Observaciones</p>
                    <p>{session.observaciones}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* History */}
      <div className="card" data-testid="cash-history">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#1A2835]">Historial de Cajas</h3>
          <Button variant="outline" className="btn-secondary gap-2" onClick={handleExportCaja} data-testid="btn-export-caja">
            <Download size={16} /> Descargar Excel
          </Button>
        </div>
        <div className="flex items-center gap-3 mb-4">
          <Calendar size={16} className="text-[#5C7083]" />
          <Button variant="ghost" size="sm" className="p-1 h-8 w-8" onClick={() => navigateHistMonth(-1)} data-testid="btn-hist-prev">
            <ChevronLeft size={16} />
          </Button>
          <span className="text-sm font-medium text-[#1A2835] min-w-[130px] text-center" data-testid="hist-month-label">
            {histMonthLabel()}
          </span>
          <Button variant="ghost" size="sm" className="p-1 h-8 w-8" onClick={() => navigateHistMonth(1)} data-testid="btn-hist-next">
            <ChevronRight size={16} />
          </Button>
          <Input type="month" value={histMonth} onChange={e => setHistMonth(e.target.value)}
            className="w-auto text-xs h-8" data-testid="input-hist-month" />
          <span className="text-xs text-[#5C7083] ml-auto">{history.length} sesiones</span>
        </div>
        {loadingHist ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#2B4257]" /></div>
        ) : history.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table w-full text-sm">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Responsable</th>
                  <th>Estado</th>
                  <th className="text-right">Ef. Inicial</th>
                  <th className="text-right">Getnet</th>
                  <th className="text-right">Efectivo</th>
                  <th className="text-right">Transfer.</th>
                  <th className="text-right">Gastos</th>
                  <th className="text-right">Ef. Final</th>
                  <th className="text-right">Diferencia</th>
                </tr>
              </thead>
              <tbody>
                {history.filter(s => s.fecha !== getTodayDate() || session?.id !== s.id).map((s) => (
                  <React.Fragment key={s.id}>
                  <tr className="cursor-pointer hover:bg-[#F5F0E8]/50 transition-colors" onClick={() => toggleSessionDetail(s.id)}
                    data-testid={`hist-session-${s.id}`}>
                    <td className="font-medium">{s.fecha}</td>
                    <td>{s.responsable}</td>
                    <td>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${s.estado_caja === 'abierta' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {s.estado_caja === 'abierta' ? 'Abierta' : 'Cerrada'}
                      </span>
                    </td>
                    <td className="text-right">{formatCurrency(s.efectivo_inicial)}</td>
                    <td className="text-right text-[#2B4257]">{formatCurrency(s.total_ingreso_getnet || 0)}</td>
                    <td className="text-right text-[#4A7C59]">{formatCurrency(s.total_ingreso_efectivo || 0)}</td>
                    <td className="text-right text-[#D98A6C]">{formatCurrency(s.total_ingreso_transferencia || 0)}</td>
                    <td className="text-right text-[#C25953]">{formatCurrency(s.total_gastos || 0)}</td>
                    <td className="text-right">{s.efectivo_final != null ? formatCurrency(s.efectivo_final) : '-'}</td>
                    <td className={`text-right font-medium ${(s.diferencia || 0) === 0 ? 'text-[#4A7C59]' : 'text-[#C25953]'}`}>
                      {s.efectivo_final != null ? formatCurrency(s.diferencia || 0) : '-'}
                    </td>
                  </tr>
                  {expandedSession === s.id && (
                    <tr>
                      <td colSpan={10} className="p-0 bg-[#FAFAF5]">
                        {loadingTxns ? (
                          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-[#2B4257]" /></div>
                        ) : sessionTxns.length > 0 ? (
                          <div className="p-3">
                            <p className="text-xs font-semibold text-[#5C7083] mb-2 px-1">Detalle de transacciones — {s.fecha}</p>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-[#E8E2D7]">
                                  <th className="text-left py-1.5 px-2 text-[#5C7083] font-medium">Hora</th>
                                  <th className="text-left py-1.5 px-2 text-[#5C7083] font-medium">Item</th>
                                  <th className="text-left py-1.5 px-2 text-[#5C7083] font-medium">Cant.</th>
                                  <th className="text-left py-1.5 px-2 text-[#5C7083] font-medium">Medio</th>
                                  <th className="text-right py-1.5 px-2 text-[#5C7083] font-medium">Ingreso</th>
                                  <th className="text-right py-1.5 px-2 text-[#5C7083] font-medium">Egreso</th>
                                  <th className="text-right py-1.5 px-2 text-[#5C7083] font-medium">Saldo</th>
                                  <th className="text-left py-1.5 px-2 text-[#5C7083] font-medium">Boleta</th>
                                  <th className="text-left py-1.5 px-2 text-[#5C7083] font-medium">Usuario</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sessionTxns.map(t => {
                                  const ing = (t.ingreso_efectivo || 0) + (t.ingreso_getnet || 0) + (t.ingreso_transferencia || 0);
                                  const eg = t.gasto_efectivo || 0;
                                  return (
                                    <tr key={t.id} className="border-b border-[#E8E2D7]/50">
                                      <td className="py-1.5 px-2 text-[#5C7083]">{(t.created_at || '').slice(11, 16)}</td>
                                      <td className="py-1.5 px-2 text-[#1A2835]">{t.item}</td>
                                      <td className="py-1.5 px-2">{t.cantidad || 1}</td>
                                      <td className="py-1.5 px-2 text-[#5C7083]">{t.medio_pago}</td>
                                      <td className="py-1.5 px-2 text-right text-[#4A7C59] font-medium">{ing > 0 ? formatCurrency(ing) : '-'}</td>
                                      <td className="py-1.5 px-2 text-right text-[#C25953]">{eg > 0 ? formatCurrency(eg) : '-'}</td>
                                      <td className="py-1.5 px-2 text-right">{formatCurrency(t.saldo_acumulado || 0)}</td>
                                      <td className="py-1.5 px-2 text-[#5C7083]">{t.boleta || '-'}</td>
                                      <td className="py-1.5 px-2 text-[#5C7083]">{t.registered_by_name || '-'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-center text-xs text-[#5C7083] py-4">Sin transacciones registradas</p>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <Wallet className="mx-auto h-10 w-10 text-[#E8E2D7] mb-3" />
            <p className="text-sm text-[#5C7083]">Sin sesiones de caja en {histMonthLabel()}</p>
          </div>
        )}
      </div>

      {/* Open Dialog */}
      <Dialog open={showOpenDialog} onOpenChange={setShowOpenDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Abrir Caja</DialogTitle></DialogHeader>
          <form onSubmit={handleOpen} className="space-y-4" data-testid="open-cash-form">
            <div>
              <Label className="form-label">Efectivo Inicial *</Label>
              <Input type="number" value={openingCash} onChange={(e) => setOpeningCash(e.target.value)}
                className="form-input" placeholder="0" min="0" required data-testid="input-opening-cash" />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowOpenDialog(false)} className="btn-secondary">Cancelar</Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="btn-confirm-open">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Abrir Caja
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transaction Dialog - INGRESO uses product dropdown, GASTO uses free text */}
      <Dialog open={showTxnDialog} onOpenChange={setShowTxnDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nueva Transaccion</DialogTitle></DialogHeader>
          <form onSubmit={handleAddTxn} className="space-y-4" data-testid="add-txn-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Tipo *</Label>
                <Select value={txnType} onValueChange={(v) => {
                  setTxnType(v);
                  setTxnData(prev => ({ ...prev, product_id: '', item: '', categoria: '', monto: v === 'cortesia' ? '0' : '' }));
                }}>
                  <SelectTrigger data-testid="select-txn-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ingreso">Ingreso / Venta</SelectItem>
                    <SelectItem value="cortesia">Cortesia (Costo $0)</SelectItem>
                    <SelectItem value="gasto">Gasto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {txnType !== 'gasto' && (
                <div>
                  <Label className="form-label">Medio de Pago</Label>
                  <Select value={txnType === 'cortesia' ? 'efectivo' : txnData.medio_pago} onValueChange={(v) => setTxnData({ ...txnData, medio_pago: v })} disabled={txnType === 'cortesia'}>
                    <SelectTrigger data-testid="select-txn-payment"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {paymentMethods.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* INGRESO / CORTESIA: product dropdown */}
            {(txnType === 'ingreso' || txnType === 'cortesia') && (
              <div>
                <Label className="form-label">Producto / Item *</Label>
                <Select value={txnData.product_id || "other"} onValueChange={handleProductSelect}>
                  <SelectTrigger data-testid="select-txn-product"><SelectValue placeholder="Seleccionar producto" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="other">Otro (ingreso manual)</SelectItem>
                    {products.filter(p => p.stock > 0).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} - {formatCurrency(p.price)} (stock: {p.stock})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!txnData.product_id && (
                  <Input className="mt-2" value={txnData.item}
                    onChange={(e) => setTxnData({ ...txnData, item: e.target.value })}
                    placeholder="Descripcion del item (ej: Plan Balance x8)" required
                    data-testid="input-txn-manual-item" />
                )}
              </div>
            )}

            {/* GASTO: free text */}
            {txnType === 'gasto' && (
              <div>
                <Label className="form-label">Descripcion del Gasto *</Label>
                <Input value={txnData.item}
                  onChange={(e) => setTxnData({ ...txnData, item: e.target.value })}
                  placeholder="Ej: Insumos limpieza, materiales, etc."
                  required data-testid="input-txn-gasto-item" />
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="form-label">Monto {txnType === 'cortesia' ? '(Cortesia)' : '*'}</Label>
                <Input type="number" value={txnType === 'cortesia' ? '0' : txnData.monto}
                  onChange={(e) => setTxnData({ ...txnData, monto: e.target.value })}
                  placeholder="0" min={txnType === 'cortesia' ? '0' : '1'}
                  required={txnType !== 'cortesia'} disabled={txnType === 'cortesia'}
                  data-testid="input-txn-amount" />
              </div>
              <div>
                <Label className="form-label">Cantidad</Label>
                <Input type="number" value={txnData.cantidad} onChange={(e) => setTxnData({ ...txnData, cantidad: e.target.value })}
                  min="1" data-testid="input-txn-qty" />
              </div>
              <div>
                <Label className="form-label">N. Boleta</Label>
                <Input value={txnData.boleta} onChange={(e) => setTxnData({ ...txnData, boleta: e.target.value })}
                  placeholder="Opcional" data-testid="input-txn-boleta" />
              </div>
            </div>
            <div>
              <Label className="form-label">Observaciones</Label>
              <Input value={txnData.observaciones} onChange={(e) => setTxnData({ ...txnData, observaciones: e.target.value })}
                placeholder="Opcional" data-testid="input-txn-obs" />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowTxnDialog(false)} className="btn-secondary">Cancelar</Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="btn-confirm-txn">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Registrar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Close Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Cerrar Caja</DialogTitle></DialogHeader>
          <form onSubmit={handleClose} className="space-y-4" data-testid="close-cash-form">
            <div className="p-4 bg-[#F5F0E8] rounded-lg">
              <p className="text-sm text-[#5C7083]">Saldo Esperado en Efectivo</p>
              <p className="text-2xl font-bold text-[#1A2835]">{formatCurrency(saldoEsperado)}</p>
            </div>
            <div>
              <Label className="form-label">Efectivo Final (contado) *</Label>
              <Input type="number" value={closingCash} onChange={(e) => setClosingCash(e.target.value)}
                placeholder="0" min="0" required data-testid="input-closing-cash" />
            </div>
            <div>
              <Label className="form-label">Observaciones</Label>
              <textarea value={closingObs} onChange={(e) => setClosingObs(e.target.value)}
                className="form-input min-h-[80px] resize-none w-full" placeholder="Notas sobre el cierre..."
                data-testid="input-closing-observations" />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowCloseDialog(false)} className="btn-secondary">Cancelar</Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="btn-confirm-close">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Cerrar Caja
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Caja;
